import api from './api';

export interface OnboardDomainResponse {
  success: boolean;
  message: string;
  zoneId?: string;
  nameservers?: string[];
  status?: string;
  nextSteps?: string[];
  alreadyOnboarded?: boolean;
  zoneInfo?: ZoneInfo;
}

export interface ZoneInfo {
  zoneId: string;
  name: string;
  status: string;
  nameservers: string[];
  account: any;
  created_on: string;
  modified_on: string;
}

export interface DomainStatusResponse {
  domain: string;
  isOnboarded: boolean;
  zoneInfo?: ZoneInfo;
  message: string;
}

export interface ZoneInfoResponse {
  found: boolean;
  zoneInfo?: ZoneInfo;
  message?: string;
}

class CloudflareApi {
  private baseURL = '/cloudflare';

  /**
   * Onboard domain to Cloudflare
   */
  async onboardDomain(domain: string): Promise<OnboardDomainResponse> {
    const response = await api.post(`${this.baseURL}/onboard`, { domain });
    return response.data;
  }

  /**
   * Check if domain is onboarded to Cloudflare
   */
  async checkDomain(domain: string): Promise<DomainStatusResponse> {
    const response = await api.get(`${this.baseURL}/check/${encodeURIComponent(domain)}`);
    return response.data;
  }

  /**
   * Get detailed zone information for a domain
   */
  async getZoneInfo(domain: string): Promise<ZoneInfoResponse> {
    const response = await api.get(`${this.baseURL}/zone/${encodeURIComponent(domain)}`);
    return response.data;
  }

  /**
   * Get DNS records for a domain
   */
  async getDNSRecords(domain: string): Promise<{ domain: string; records: any[]; count: number }> {
    const response = await api.get(`${this.baseURL}/dns/${encodeURIComponent(domain)}`);
    return response.data;
  }

  /**
   * Sync DNS records to Cloudflare
   */
  async syncDNSRecords(
    domain: string,
    records: Array<{
      type: string;
      name: string;
      content: string;
      ttl?: number;
      proxied?: boolean;
    }>
  ): Promise<{ domain: string; success: number; failed: number; errors: string[]; message: string }> {
    const response = await api.post(`${this.baseURL}/dns/sync/${encodeURIComponent(domain)}`, { records });
    return response.data;
  }

  /**
   * Create a redirect rule for a domain
   * Redirects all incoming requests to a target URL with 301 status code
   */
  async createRedirectRule(
    domain: string,
    targetUrl: string,
    zoneId?: string
  ): Promise<{
    ruleId: string;
    message: string;
    domain: string;
    targetUrl: string;
  }> {
    const response = await api.post(`${this.baseURL}/redirect/create`, {
      domain,
      targetUrl,
      zoneId,
    });
    return response.data;
  }

  /**
   * Delete a redirect rule for a domain
   */
  async deleteRedirectRule(
    domain: string,
    zoneId?: string
  ): Promise<{
    success: boolean;
    message: string;
    domain: string;
  }> {
    const response = await api.delete(`${this.baseURL}/redirect/delete`, {
      data: {
        domain,
        zoneId,
      },
    });
    return response.data;
  }
}

export const cloudflareApi = new CloudflareApi();

